// src/contexts/WebSocketContext.tsx
// Contexte React pour gérer les connexions WebSocket
import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import websocketService, { WebSocketEventType, WebSocketMessage } from '../services/websocket';
import { useAuth } from '../hooks/useAuth';
import { queryKeys } from '../lib/queryClient';

interface WebSocketContextType {
  isConnected: boolean;
  connectionState: 'connecting' | 'connected' | 'disconnected' | 'reconnecting';
  subscribe: <T>(type: WebSocketEventType, handler: (payload: T) => void) => () => void;
  send: <T>(type: WebSocketEventType, payload: T) => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

// URL WebSocket (à configurer selon l'environnement)
const getWebSocketUrl = (): string => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const wsHost = apiUrl.replace(/^https?:\/\//, '');
  return `${wsProtocol}://${wsHost}/ws`;
};

interface WebSocketProviderProps {
  children: React.ReactNode;
}

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children }) => {
  const { authState } = useAuth();
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'disconnected' | 'reconnecting'>('disconnected');
  const subscriptionsRef = useRef<Map<string, () => void>>(new Map());

  // Gestionnaire de messages pour mettre à jour le cache React Query
  const handleWebSocketMessage = useCallback((message: WebSocketMessage) => {
    switch (message.type) {
      case 'new_post':
        // Invalider le cache des posts pour forcer un refetch
        queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
        break;

      case 'post_updated':
        // Mettre à jour le post spécifique dans le cache
        queryClient.invalidateQueries({
          queryKey: queryKeys.posts.detail((message.payload as { id: number }).id)
        });
        queryClient.invalidateQueries({ queryKey: queryKeys.posts.infinite() });
        break;

      case 'post_deleted':
        // Supprimer le post du cache
        queryClient.invalidateQueries({ queryKey: queryKeys.posts.all });
        break;

      case 'new_comment':
        // Invalider les commentaires du post
        const commentPayload = message.payload as { post_id: number };
        queryClient.invalidateQueries({
          queryKey: queryKeys.posts.comments(commentPayload.post_id)
        });
        break;

      case 'new_reaction':
      case 'reaction_removed':
        // Mettre à jour les réactions
        queryClient.invalidateQueries({ queryKey: queryKeys.posts.infinite() });
        break;

      case 'new_notification':
        // Invalider les notifications
        queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
        break;

      default:
        break;
    }
  }, [queryClient]);

  // Connexion/Déconnexion basée sur l'authentification
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (authState.isAuthenticated && token) {
      setConnectionState('connecting');

      websocketService.connect({
        url: getWebSocketUrl(),
        token: token,
        reconnectAttempts: 5,
        reconnectInterval: 3000,
        heartbeatInterval: 30000,
        onOpen: () => {
          setIsConnected(true);
          setConnectionState('connected');
        },
        onClose: () => {
          setIsConnected(false);
          setConnectionState('disconnected');
        },
        onError: () => {
          setConnectionState('disconnected');
        },
        onMessage: handleWebSocketMessage,
        onReconnect: () => {
          setConnectionState('reconnecting');
        },
      });

      return () => {
        websocketService.disconnect();
        setIsConnected(false);
        setConnectionState('disconnected');
      };
    } else {
      websocketService.disconnect();
      setIsConnected(false);
      setConnectionState('disconnected');
    }
  }, [authState.isAuthenticated, handleWebSocketMessage]);

  // Fonction pour s'abonner à un type d'événement
  const subscribe = useCallback(<T,>(type: WebSocketEventType, handler: (payload: T) => void): (() => void) => {
    const unsubscribe = websocketService.subscribe(type, handler);
    const key = `${type}-${Date.now()}`;
    subscriptionsRef.current.set(key, unsubscribe);

    return () => {
      unsubscribe();
      subscriptionsRef.current.delete(key);
    };
  }, []);

  // Fonction pour envoyer un message
  const send = useCallback(<T,>(type: WebSocketEventType, payload: T): void => {
    websocketService.send(type, payload);
  }, []);

  // Fonction pour forcer une reconnexion
  const reconnect = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (authState.isAuthenticated && token) {
      websocketService.disconnect();
      setConnectionState('connecting');

      setTimeout(() => {
        websocketService.connect({
          url: getWebSocketUrl(),
          token: token,
          onOpen: () => {
            setIsConnected(true);
            setConnectionState('connected');
          },
          onClose: () => {
            setIsConnected(false);
            setConnectionState('disconnected');
          },
          onMessage: handleWebSocketMessage,
        });
      }, 100);
    }
  }, [authState.isAuthenticated, handleWebSocketMessage]);

  const value: WebSocketContextType = {
    isConnected,
    connectionState,
    subscribe,
    send,
    reconnect,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Hook pour utiliser le contexte WebSocket
export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

// Hook pour s'abonner à un événement spécifique
export const useWebSocketEvent = <T,>(
  type: WebSocketEventType,
  handler: (payload: T) => void,
  deps: React.DependencyList = []
): void => {
  const { subscribe } = useWebSocket();

  useEffect(() => {
    const unsubscribe = subscribe(type, handler);
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe, type, ...deps]);
};

export default WebSocketProvider;
